import isEmpty from 'lodash/isEmpty';
import type { Column, WorkflowOld } from '@atlassian/jira-business-board-workflow-issues';
import { useIssueTypesAndFields } from '@atlassian/jira-business-entity-project';
import { useProject } from '@atlassian/jira-business-entity-project-hook';
import {
	useExperienceStart,
	useExperienceSuccess,
	useExperienceFail,
} from '@atlassian/jira-business-experience-tracking';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { ff } from '@atlassian/jira-feature-flagging';
import { isClientFetchError } from '@atlassian/jira-fetch';
import { useFlagService } from '@atlassian/jira-flags';
import { useCloudId } from '@atlassian/jira-tenant-context-controller';
import { COLUMN_ACTION_DELETE_EXPERIENCE } from '../../../common/constants';
import type { BoardColumn, Status } from '../../../common/types';
import { useBoardStoreState, useBoardStoreActions } from '../../../services/board-store';
import {
	useWorkflowsV2,
	mapStatusesToStatusUpdateObjects,
	mapWorkflowToWorkflowUpdateObject,
} from '../../../services/workflow-v2';
import { getBoardDataWithMultipleWorkflows } from '../../../utils/board-fetch-old';
import { useActiveWorkflow } from '../../active-workflow-old';
import { useBoardData } from '../../board-data';
import { useRefetchBoardData as useRefetchBoardDataOld } from '../../board-data-old';
import messages from '../messages';
import {
	getColumnActionFailedFlag,
	removeWorkflowAssociatedWithSubtaskOnly,
	fireColumnExperienceTracking,
	isFilteredNetworkError,
} from '../utils';

const getWorkflowById = (
	workflowId?: string,
	// @ts-expect-error - TS1016 - A required parameter cannot follow an optional parameter.
	workflows: WorkflowOld[],
): WorkflowOld | null => {
	if (workflowId == null) {
		return workflows[0];
	}
	return workflows.find((workflow) => workflow.workflowId === workflowId) || workflows[0];
};

/** @deprecated Remove when jwm.board-awesome-enhancements is cleaned up */
export const useDeleteColumn = () => {
	const { fetchWorkflowsV2, validateAndUpdateWorkflowsV2 } = useWorkflowsV2();
	const { columns, workflows } = useBoardStoreState();
	const { deleteColumn, setColumnsAndWorkflows, setDeleteColumnInProgress } =
		useBoardStoreActions();
	const revertOptimisticUpdate = () => {
		setColumnsAndWorkflows(columns, workflows);
	};

	const startExperience = useExperienceStart(COLUMN_ACTION_DELETE_EXPERIENCE);
	const markExperienceSuccess = useExperienceSuccess(COLUMN_ACTION_DELETE_EXPERIENCE);
	const markExperienceFailed = useExperienceFail(COLUMN_ACTION_DELETE_EXPERIENCE);

	const {
		data: { issueTypes },
	} = useIssueTypesAndFields({
		issueOperation: 'VIEW',
	});
	const cloudId = useCloudId();
	const projectData = useProject();
	const projectId = String(projectData.id);
	const projectKey = projectData.key;

	let refetchBoardData: () => Promise<void>;
	if (ff('jwm.board-awesome-enhancements')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		({ refetch: refetchBoardData } = useBoardData());
	} else {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		refetchBoardData = useRefetchBoardDataOld();
	}

	const { showFlag } = useFlagService();
	const subtaskIssueTypeId = issueTypes.find((issueType) => issueType.subtask)?.id;
	const issueTypeIdNotSubtask = issueTypes.find((issueType) => !issueType.subtask)?.id;
	const [selectedWorkflow] = useActiveWorkflow();

	const deleteBoardColumn = async ({
		columnToDelete,
		toStatus,
	}: {
		columnToDelete: BoardColumn;
		toStatus: Status;
	}) => {
		startExperience();

		const toColumnData = { loading: true };
		deleteColumn(columnToDelete, toStatus, toColumnData);
		const columnToDeleteStatusId = columnToDelete.statusId.toString();
		const toStatusId = toStatus.id.toString();
		let updatedWorkflowsResponse;
		const MAX_POLL_COUNT = 5;

		// fetch workflow data from workflow API
		let workflowsData;
		try {
			workflowsData = await fetchWorkflowsV2({
				cloudId,
				projectAndIssueTypes: issueTypes.map((it) => ({
					projectId,
					issueTypeId: it.id,
				})),
			});
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			showFlag(
				getColumnActionFailedFlag({
					messageTitle: messages.errorFlagTitleDeleteColumn,
					action: 'delete',
					error,
					issueTypeIdNotSubtask,
					projectKey,
				}),
			);
			revertOptimisticUpdate();

			if ((error && isClientFetchError(error)) || isFilteredNetworkError(error)) {
				markExperienceSuccess();
			} else {
				markExperienceFailed('failed to fetch workflow data', error);
			}

			return;
		}

		if (!workflowsData) {
			revertOptimisticUpdate();

			markExperienceFailed('workflow data is empty', new Error('workflow data is empty'));
			return;
		}

		// remove subtask workflow
		const filteredWorkflowsData = removeWorkflowAssociatedWithSubtaskOnly(
			workflowsData,
			subtaskIssueTypeId,
		);

		const { statuses, workflows: boardWorkflows } = filteredWorkflowsData;
		const workflow = boardWorkflows[0];
		const wfstatusToDeleteIndex = workflow.statuses.findIndex(
			(status) => status.statusReference === columnToDeleteStatusId,
		);
		workflow.statuses.splice(wfstatusToDeleteIndex, 1);

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		workflow.transitions = workflow.transitions.reduce<Array<any>>((acc, next) => {
			// delete GLOBAL transitions associated with the column being deleted
			if (next.type === 'GLOBAL' && next.to.statusReference === columnToDeleteStatusId) {
				return acc;
			}
			// update the INITIAL transition
			if (next.type === 'INITIAL' && next.to.statusReference === columnToDeleteStatusId) {
				const intialTransition = { ...next };
				intialTransition.to.statusReference = toStatusId;
				acc.push(intialTransition);
				return acc;
			}
			// remove any DIRECTED transitions which reference columnToDeleteStatusId
			if (
				next.type === 'DIRECTED' &&
				(next.to.statusReference === columnToDeleteStatusId ||
					next.from.some((directedFrom) => directedFrom.statusReference === columnToDeleteStatusId))
			) {
				return acc;
			}
			acc.push(next);
			return acc;
		}, []);

		const associatedProjectIndex = workflow.associatedWith.findIndex(
			(association) => association.project.id === projectId,
		);

		if (associatedProjectIndex !== -1) {
			workflow.statusMappings = workflow.associatedWith[associatedProjectIndex]?.issueTypes?.map(
				(type: string) => ({
					issueTypeId: type,
					projectId,
					statusMigrations: [
						{
							oldStatusReference: columnToDeleteStatusId,
							newStatusReference: toStatusId,
						},
					],
				}),
			);
		}

		if (columnToDelete.isDone) {
			// update new status to DONE
			const statusToConvertToDoneIndex = statuses.findIndex(
				(status) => status.name === toStatus.name,
			);

			if (statusToConvertToDoneIndex !== -1) {
				statuses[statusToConvertToDoneIndex].statusCategory = 'DONE';
			}
		}

		const updatePayload = {
			statuses: mapStatusesToStatusUpdateObjects(statuses),
			workflows: [mapWorkflowToWorkflowUpdateObject(workflow)],
		};

		// validate the payload and update the worfklow
		try {
			const response = await validateAndUpdateWorkflowsV2({
				cloudId,
				payload: {
					payload: updatePayload,
					validationOptions: { levels: ['ERROR'] },
				},
			});
			updatedWorkflowsResponse = response;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			revertOptimisticUpdate();
			showFlag(
				getColumnActionFailedFlag({
					messageTitle: messages.errorFlagTitleDeleteColumn,
					action: 'delete',
					error,
					issueTypeIdNotSubtask,
					projectKey,
					statusCode: error.statusCode,
				}),
			);
			fireColumnExperienceTracking({
				markExperienceSuccess,
				markExperienceFailed,
				error,
				errorMessage: 'failed to validate and update workflow',
			});
			return;
		}

		const fetchBoardWorkflowsDeleteStatus = async () => {
			try {
				const response = await getBoardDataWithMultipleWorkflows(projectId, false);
				const workflowsResponse = await response.json();
				const boardDataResponse =
					(!isEmpty(workflowsResponse.data) &&
						getWorkflowById(selectedWorkflow, workflowsResponse.data)) ||
					null;
				return boardDataResponse?.columns?.every((col: Column) => col.name !== columnToDelete.name);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics({
					meta: {
						id: 'fetchBoardData',
						packageName: 'jiraWorkManagementBoard',
						teamName: 'wanjel',
					},
					attributes: {
						message: 'Failed to fetch board data and workflows',
					},
					error,
					sendToPrivacyUnsafeSplunk: true,
				});

				showFlag(
					getColumnActionFailedFlag({
						messageTitle: messages.errorFlagTitleDeleteColumn,
						action: 'delete',
						error,
						issueTypeIdNotSubtask,
						projectKey,
					}),
				);
				markExperienceFailed('Failed to fetch board data and workflows', error);
				// return true to prevent polling below
				return true;
			}
		};

		let timerId: ReturnType<typeof setTimeout> | undefined;
		let count = 0;
		//  poll boardData to make sure that workflows are correctly updated
		const pollFetchBoardWorkflows = async () => {
			if (count === MAX_POLL_COUNT) {
				showFlag(
					getColumnActionFailedFlag({
						messageTitle: messages.deletePollingTimeoutTitle,
						action: 'delete-polling',
						issueTypeIdNotSubtask,
						projectKey,
					}),
				);
				fireErrorAnalytics({
					meta: {
						id: 'pollFetchBoardWorkflows',
						packageName: 'jiraWorkManagementBoard',
						teamName: 'wanjel',
					},
					attributes: {
						message: 'Poll fetchBoardWorkflows timed out after delete operation',
					},
					sendToPrivacyUnsafeSplunk: true,
				});
				revertOptimisticUpdate();

				markExperienceFailed(
					'Delete column polling reaches the max poll count',
					new Error('Delete column polling reaches the max poll count'),
				);
				return;
			}

			const isDeletedColumnHasBeenRemovedInWorkflow = await fetchBoardWorkflowsDeleteStatus();
			timerId && clearTimeout(timerId);
			timerId = undefined;

			if (isDeletedColumnHasBeenRemovedInWorkflow === true) {
				await refetchBoardData();

				setDeleteColumnInProgress(false);

				markExperienceSuccess();
				return;
			}
			const ms = 2 ** count * 1000;
			count += 1;
			timerId = setTimeout(pollFetchBoardWorkflows, ms);
		};

		if (updatedWorkflowsResponse && updatedWorkflowsResponse.migrationId != null) {
			await pollFetchBoardWorkflows();
		}
	};

	return {
		deleteBoardColumn,
	};
};
